/**
 * useTrendCompare · Module A 投放趋势对比数据 Hook
 *
 * 接口契约：specs/多游戏投放对比页/api/timeline.md
 * URL：POST /api/v1/marketing/ad_creative_multi/timeline
 *
 * 设计：
 * - **每次切指标都重新请求**（不在前端做合并/缓存，后端已支持 `dimension_values` 单维度返回）
 * - metric 映射：
 *   - 4 个 trend 指标（all_creative / new_creative / all_material / new_material）
 *     → `metric=creatives_trend`，`dimension_values=[metric]`，后端只返这一条
 *   - `engagement_stat` → `metric=engagement_stat`，按 `dimension_value="engagement_stat"` 取汇总
 * - **raw → series 转换**：把三层 `times[] + games[].timeline[].values[]` 摊平为
 *   `ITrendSeries[] = [{ game_id, values: [{ date, value }] }]`
 *   接口已保证 `timeline.length === times.length` 按索引对齐，缺失维度时 value = 0
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import { getTimeline, getDimensionSummary } from '../services';
import { IFilter } from '../types/filter';
import { TMetric } from '../types/common';
import {
  ITimelineRawParams,
  ITimelineRawResponse,
  ITrendSeries,
  TTimelineMetric,
} from '../types/trend';
import {
  IDimensionSummaryParams,
  IDimensionSummaryResponse,
} from '../types/dimensionSummary';

export interface IUseTrendCompareArgs {
  filter: IFilter;
  metric: TMetric;
}

const EMPTY_RAW: ITimelineRawResponse = {
  times: [],
  timeline_of_start_time: '',
  timeline_of_end_time: '',
  games: [],
};

/**
 * 前端 metric → 接口 metric / dimension 映射
 */
function resolveApiMetric(metric: TMetric): {
  apiMetric: TTimelineMetric;
  pickDimension: string;
  dimensionValues: string[];
} {
  if (metric === 'engagement_stat') {
    return {
      apiMetric: 'engagement_stat',
      pickDimension: 'engagement_stat',
      dimensionValues: [],
    };
  }
  // 4 个 creatives_trend 指标：单维度查询
  return {
    apiMetric: 'creatives_trend',
    pickDimension: metric,
    dimensionValues: [metric],
  };
}

/**
 * raw → 前端 ITrendSeries[]
 * 按 `dimension_value === pickDimension` 在每个时间点取值；缺失时取 0
 */
function rawToSeries(
  raw: ITimelineRawResponse,
  pickDimension: string,
): ITrendSeries[] {
  const times = raw.times || [];
  return (raw.games || []).map(g => {
    const listItem = g.list?.find(v => v.dimension_value === pickDimension);
    return {
      game_id: g.game_id,
      values: times.map((time, idx) => {
        const item = g.timeline?.[idx];
        const found = item?.values?.find(
          v => v.dimension_value === pickDimension,
        );
        return { date: time, value: found?.value ?? 0 };
      }),
      listValue: listItem?.value ?? 0,
    };
  });
}

export function useTrendCompare({ filter, metric }: IUseTrendCompareArgs) {
  const [raw, setRaw] = useState<ITimelineRawResponse>(EMPTY_RAW);
  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  /** 接口请求参数：filter / metric 任一变化都重发 */
  const params = useMemo<ITimelineRawParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    const { apiMetric, dimensionValues } = resolveApiMetric(metric);
    return {
      ...filter,
      metric: apiMetric,
      dimension_values: dimensionValues,
    };
  }, [filter, metric]);

  const request = useCallback(
    (nextParams: ITimelineRawParams) => {
      wrapperCancelRequest(getTimeline, nextParams)
        .then((response: any) => {
          setRaw({
            times: response.times || [],
            timeline_of_start_time: response.timeline_of_start_time || '',
            timeline_of_end_time: response.timeline_of_end_time || '',
            games: (response.games || []).map((g: any) => ({
              ...g,
              list: g.list || [],
            })),
          });
        })
        .catch(() => {
          // 请求被取消或失败时由 wrapperCancelRequest 内部处理
        });
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    setRaw(EMPTY_RAW);
    if (!params) {
      return;
    }
    request(params);
  }, [params, request]);

  /** raw → series（pickDimension 与请求 dimension_values 对齐） */
  const series = useMemo(() => {
    const { pickDimension } = resolveApiMetric(metric);
    return rawToSeries(raw, pickDimension);
  }, [raw, metric]);

  return {
    series,
    /** 公共时间轴（备用） */
    times: raw.times,
    loading,
  };
}

// ─────────────────────────────────────────────────────────
// Trend Pie · 饼图（dimension_summary 接口；区间日均，无环比）
// ─────────────────────────────────────────────────────────

export interface IUseTrendPieSummaryArgs {
  filter: IFilter;
  metric: TMetric;
}

const EMPTY_PIE_RESPONSE: IDimensionSummaryResponse = { games: [] };

/**
 * useTrendPieSummary · Trend 饼图数据 hook
 *
 * ⚠️ 本 hook 由 `TrendPieChart` 组件内部直接调用，
 * 仅在 chartType === 'pie' 时挂载 → 不需要 enabled 参数；
 * 组件卸载时 useCancelRequestHook 会自动取消 in-flight 请求。
 *
 * 返回 `pieSeries: ITrendSeries[]`（仅 game_id + listValue），TrendPieChart
 * 内部仅消费 listValue（不依赖 values[]）。
 *
 * metric 映射与 useTrendCompare 一致：
 * - 4 个 creatives_trend dimension（all_creative ...）→ metric=creatives_trend, dimension_values=[metric]
 * - engagement_stat → metric=engagement_stat（无 dimension_value 过滤）
 */
export function useTrendPieSummary({
  filter,
  metric,
}: IUseTrendPieSummaryArgs) {
  const [data, setData] =
    useState<IDimensionSummaryResponse>(EMPTY_PIE_RESPONSE);
  const { wrapperCancelRequest, loading } = useCancelRequestHook();

  const params = useMemo<IDimensionSummaryParams | null>(() => {
    if (!filter?.start_time || !filter?.end_time || !filter?.game_ids?.length)
      return null;
    const { apiMetric, dimensionValues } = resolveApiMetric(metric);
    return {
      game_ids: filter.game_ids || [],
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
      metric: apiMetric,
      dimension_values: dimensionValues,
      need_link: false,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, metric]);

  const request = useCallback(
    (nextParams: IDimensionSummaryParams) => {
      wrapperCancelRequest(getDimensionSummary, nextParams)
        .then((response: any) => {
          if (!response) return;
          // pie 只需要每个游戏的 list 累加值；不消费 dimension_max / link_time
          setData({
            games: response.games || [],
          });
        })
        .catch(() => {});
    },
    [wrapperCancelRequest],
  );

  useEffect(() => {
    if (!params) {
      setData(EMPTY_PIE_RESPONSE);
      return;
    }
    request(params);
  }, [params, request]);

  /**
   * 派生 pieSeries：每个游戏在区间内的指标聚合值
   *
   * - 4 个 creatives_trend dimension（单维度查询）：list 只 1 项 → 累加 = 该项 value
   * - engagement_stat（多维度=渠道，无单一汇总行）：list 多项 → 累加 = 各渠道日均之和
   *
   * dimension_summary 的 list 已是区间日均（不像 timeline 是每日值），直接按 game 累加即可。
   */
  const pieSeries = useMemo<ITrendSeries[]>(() => {
    return (data.games || []).map(g => ({
      game_id: g.game_id,
      values: [],
      listValue: (g.list || []).reduce(
        (acc, v) => acc + (Number.isFinite(v.value) ? v.value : 0),
        0,
      ),
    }));
  }, [data]);

  return { pieSeries, loading };
}
